import React, { useMemo } from "react";
import { v4 as uuid } from "uuid";
import { Button } from "../../components/ui/button";
import { PlusIcon, Trash2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import useServiceConfigsValidation from "../hooks/useServiceConfigsValidation";
import useRulesValidation from "../hooks/useRulesValidation";
import ServiceSelector from "../components/ServiceSelector";
import { RuleCard } from "../components/rules/RuleCard";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card";
import toast from "../../components/ui/use-toast";
/* -------------------------------------------------------------------------- */
/*                       Constants & helper utilities                         */
/* -------------------------------------------------------------------------- */

const WEEK = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

/**
 * Convert an array of Week day‑keys into a fully‑initialised hash.
 */
const emptyWeekHash = () => WEEK.reduce((acc, d) => ({ ...acc, [d]: 0 }), {});

/* -------------------------------------------------------------------------- */
/*                   <MinEmployeesConfigCard /> (inner)                       */
/* -------------------------------------------------------------------------- */

function MinEmployeesConfigCard({
  cfg,
  services,
  onUpdate,
  onDelete,
  error,
  disabledServiceIds = [],
}) {
  /* ------------ field helpers ------------- */
  const setField = (key, val) => onUpdate({ ...cfg, [key]: val });
  const setDay = (day, val) =>
    onUpdate({
      ...cfg,
      minEmployeesPerDayByWeekday: {
        ...cfg.minEmployeesPerDayByWeekday,
        [day]: val,
      },
    });

  /* ------------------ UI ------------------ */
  return (
    <Card className="border shadow-sm overflow-visible">
      {/* header */}
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base">Min employees</CardTitle>
        </div>
        <Button variant="ghost" size="icon" onClick={() => onDelete(cfg.id)}>
          <Trash2 size={16} />
        </Button>
      </CardHeader>

      {/* body */}
      <CardContent className="space-y-6 pt-0 pb-4">
        {/* assignment */}
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="checkbox"
            checked={cfg.applyToAll}
            onChange={(e) => setField("applyToAll", e.target.checked)}
          />
          Apply to all services
        </label>

        {!cfg.applyToAll && (
          <ServiceSelector
            services={services}
            selected={cfg.services}
            onChange={(list) => setField("services", list)}
            disabledIds={disabledServiceIds}
          />
        )}

        {/* weekday inputs */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {WEEK.map((d) => (
            <label key={d} className="form-label">
              {d.toUpperCase()}
              <Input
                type="number"
                min={0}
                className="form-input"
                value={cfg.minEmployeesPerDayByWeekday[d] ?? 0}
                onChange={(e) => setDay(d, +e.target.value)}
              />
            </label>
          ))}
        </div>

        {error && <p className="text-xs text-red-500">{error}</p>}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*                         Step‑3 Scheduling Rules                            */
/* -------------------------------------------------------------------------- */

export default function Step3SchedulingRules({
  schedule = {},
  setSchedule,
  employees = [], // only SERVICE_PROVIDER employees should be passed in parent
  services = [],
  onSave,
  hasGlobalErrors = false,
}) {
  /* ──────────────── State slices ───────────────── */
  const bmCfg = schedule.businessManagedConfig ?? {};

  /* -------- Time‑off notice -------- */
  const timeOffNotice = bmCfg.timeOffRequestNoticeDays ?? 0;

  /* -------- Minimum‑employees configs -------- */
  const minEmpCfgs = Array.isArray(bmCfg.minEmployeeConfigs)
    ? bmCfg.minEmployeeConfigs
    : [];

  /* -------- Scheduling Rules -------- */
  const rules = Array.isArray(bmCfg.rules) ? bmCfg.rules : [];

  /* ─────────────── Validation ─────────────── */
  const {
    errors: minEmpSvcErrors,
    validateAll: validateMinEmpSvcConfigs,
  } = useServiceConfigsValidation(minEmpCfgs, services, { checkCoverage: false });
  const {
    errors: ruleErrors,
    validateAll: validateRules,
  } = useRulesValidation(rules, employees);
  const timeOffError     = timeOffNotice <= 0;                 // ① days-in-advance > 0
  const disableAddConfig = minEmpCfgs.length >= services.length;
  const disableAddRule   = rules.length     >= employees.length;
  const duplicates = useMemo(() => {
    const seen = {};
    const dups = [];
    minEmpCfgs.forEach((c) => {
      const key = JSON.stringify(c.minEmployeesPerDayByWeekday || {});
      if (seen[key]) {
        dups.push({
          id: c.id,
          msg: "Duplicate config – select both services in one config instead",
        });
      }
      seen[key] = true;
    });
    return dups;
  }, [minEmpCfgs]);
  const dayErrors = useMemo(() => {
    const errs = [];
    for (const c of minEmpCfgs) {
      const days = c.minEmployeesPerDayByWeekday || {};
      const hasAnyPositive = Object.values(days).some((v) => Number(v) > 0);
      if (!hasAnyPositive) {
        errs.push({ id: c.id, msg: "Enter a number > 0 for at least one day" });
      }
    }
    return errs;
  }, [minEmpCfgs]);
  const minEmpErrors = [...minEmpSvcErrors, ...duplicates, ...dayErrors];
  /* ─────────────── Helper setters ─────────────── */
  const upsertCfg = (cfg) =>
    setSchedule((prev) => {
      const prevCfgs = Array.isArray(prev.businessManagedConfig?.minEmployeeConfigs)
        ? prev.businessManagedConfig.minEmployeeConfigs
        : [];
      const arr = [...prevCfgs];
      const i = arr.findIndex((c) => c.id === cfg.id);
      i === -1 ? arr.push(cfg) : (arr[i] = cfg);
      return {
        ...prev,
        businessManagedConfig: {
          ...prev.businessManagedConfig,
          minEmployeeConfigs: arr,
        },
      };
    });

  const deleteCfg = (id) =>
    setSchedule((prev) => ({
      ...prev,
      businessManagedConfig: {
        ...prev.businessManagedConfig,
        minEmployeeConfigs: minEmpCfgs.filter((c) => c.id !== id),
      },
    }));

  const addCfg = () =>
    upsertCfg({
      id: uuid(),
      applyToAll: false,
      services: [],
      minEmployeesPerDayByWeekday: emptyWeekHash(),
    });

  const upsertRule = (rule) =>
    setSchedule((prev) => {
      const prevRules = Array.isArray(prev.businessManagedConfig?.rules)
        ? prev.businessManagedConfig.rules
        : [];
      const arr = [...prevRules];
      const i = arr.findIndex((r) => r.id === rule.id);
      i === -1 ? arr.push(rule) : (arr[i] = rule);
      return {
        ...prev,
        businessManagedConfig: {
          ...prev.businessManagedConfig,
          rules: arr,
        },
      };
    });

  const deleteRule = (id) =>
    setSchedule((prev) => ({
      ...prev,
      businessManagedConfig: {
        ...prev.businessManagedConfig,
        rules: rules.filter((r) => r.id !== id),
      },
    }));

  const addRule = () =>
    upsertRule({
      id: uuid(),
      applyToAll: false,
      employees: [],
      expanded: true,
      config: {
        employeeLimits: {
          minHoursPerWeek: 0,
          maxHoursPerWeek: 40,
          minDaysOffPerWeek: 0,
          maxDaysOffPerYear: 0,
        },
        shiftRules: {
          preferredShiftStartTimes: [],
          enforceConsistentShiftTimes: false,
        },
      },
    });

  const updateTimeOffNotice = (val) =>
    setSchedule((prev) => ({
      ...prev,
      businessManagedConfig: {
        ...prev.businessManagedConfig,
        timeOffRequestNoticeDays: val,
      },
    }));

  /* ─────────────── Utility derived values ─────────────── */
  const usedServiceIds = useMemo(() => {
    const map = {};
    for (const cfg of minEmpCfgs) {
      if (cfg.applyToAll) continue; // handled via validation
      for (const id of cfg.services || []) map[id] = (map[id] || 0) + 1;
    }
    return map;
  }, [minEmpCfgs]);

  const usedEmployeeIds = useMemo(() => {
    const map = {};
    for (const r of rules) {
      if (r.applyToAll) continue;
      for (const id of r.employees || []) map[id] = (map[id] || 0) + 1;
    }
    return map;
  }, [rules]);
  /* Any validation problems?  Hide the save button when true */
  const hasValidationErrors =             /* NEW aggregate */
    hasGlobalErrors     ||
    timeOffError        ||
    minEmpErrors.length > 0 ||
    ruleErrors.length   > 0;
  const applyToAllExistsCfg = minEmpCfgs.some((c) => c.applyToAll);
  const applyToAllExistsRule = rules.some((r) => r.applyToAll);

  /* ─────────────── Save handler ─────────────── */
  const handleSave = () => {
    if (timeOffError) {
      toast.error('Days-in-advance must be greater than 0');
      return;
    }

    const validCfgs  = validateMinEmpSvcConfigs();
    const validRules = validateRules();
    if (validCfgs && validRules) onSave?.();
  }


  /* ─────────────── UI ─────────────── */
  return (
    <div className="space-y-8">
      {/* ───── Global time‑off settings ───── */}
      <div>
        <label className="form-label">
          Days in advance to request time off
          <Input
            type="number"
            min={0}
            className="form-input mt-1"
            value={timeOffNotice}
            onChange={(e) => updateTimeOffNotice(+e.target.value)}
          />
        </label>
      {/* ③ inline error message */}
      {timeOffError && (
        <p className="text-xs text-red-500 mt-1">
          Days in advance must be greater than&nbsp;0
        </p>
      )}
      </div>

      {/* ───── Minimum Employees Configs ───── */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Minimum Employees Configs</h2>
        <Button
          onClick={addCfg}
          disabled={applyToAllExistsCfg || disableAddConfig}
          title={
            applyToAllExistsCfg
              ? '“Apply to all” already exists'
              : disableAddConfig
              ? 'You already have a config for every service'
              : ''
          }
        >
          <PlusIcon size={16} className="mr-2" />
          Add Config
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {minEmpCfgs.map((cfg) => {
          const disabledIds = Object.keys(usedServiceIds).filter(
            (id) => !cfg.services.includes(id)
          );
          const err = minEmpErrors.find((e) => e.id === cfg.id)?.msg;
          return (
            <MinEmployeesConfigCard
              key={cfg.id}
              cfg={cfg}
              services={services}
              onUpdate={upsertCfg}
              onDelete={deleteCfg}
              error={err}
              disabledServiceIds={disabledIds}
            />
          );
        })}
      </div>

      {/* ───── Scheduling Rules ───── */}
      <div className="flex justify-between items-center pt-4 border-t">
        <h2 className="text-xl font-semibold">Scheduling Rules</h2>
        <Button
          onClick={addRule}
          disabled={applyToAllExistsRule || disableAddRule}
          title={
            applyToAllExistsRule
              ? '“Apply to all” already exists'
              : disableAddRule
              ? 'You already have a rule for every employee'
              : ''
          }
        >
          <PlusIcon size={16} className="mr-2" />
          Add Rule
        </Button>
      </div>
      {[...new Set(
        ruleErrors
          .filter(e => e.ruleId === '*')
          .map(e => e.msg)
      )].map((msg, i) => (
        <p key={i} className="text-xs text-red-500 mt-2">
          {msg}
        </p>
      ))}

      <div className="grid md:grid-cols-2 gap-4">
        {rules.map((rule) => {
          const disabledIds = Object.keys(usedEmployeeIds).filter(
            (id) => !rule.employees.includes(id)
          );
const errMsgs = ruleErrors                // <- ALL messages for this rule
  .filter(e => e.ruleId === rule.id)
  .map(e => e.msg);
            return (
            <RuleCard
              key={rule.id}
              rule={rule}
              employees={employees}
              onUpdate={upsertRule}
              onDelete={deleteRule}
              error={errMsgs}
              disabledEmployeeIds={disabledIds}
            />
          );
        })}
      </div>

      {!hasValidationErrors && (                              /* NEW logic */
        <div className="flex justify-end pt-6 border-t">
          <Button onClick={handleSave}>Save Configuration</Button>
        </div>
      )}
    </div>
  );
}
